import React, { useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, ZoomControl } from 'react-leaflet';
import WetsuitRecommendation from './WetsuitRecommendation';
import {
  degreesToDirection,
  formatForecastDate,
  formatHourLabel,
  generateSurfReport,
  getSurfAssessment,
  getWindCategory,
  getWindCategoryLabel,
  generalIcons
} from './utils';

const labels = {
  english: {
    title: 'Spot map',
    subtitle: '57°04\'51.2"N 12°15\'27.7"E',
    coordinates: 'Coordinates',
    wind: 'Wind',
    direction: 'Direction',
    type: 'Orientation',
    calm: 'Calm',
    from: 'from',
    timeline: '24-hour wind timeline',
    selectedDate: 'Selected date',
    selectedHour: 'Selected hour',
    live: 'Live now',
    airTemperature: 'Air temperature',
    waveHeight: 'Wave height',
    swell: 'Swell',
    score: 'Surf score',
    report: 'Condition report',
    wetsuit: 'Wetsuit recommendation',
    wetsuitNote: 'Based on the current calculated water temperature and season.',
  },
  swedish: {
    title: 'Spotkarta',
    subtitle: '57°04\'51.2"N 12°15\'27.7"E',
    coordinates: 'Koordinater',
    wind: 'Vind',
    direction: 'Riktning',
    type: 'Orientering',
    calm: 'Lugnt',
    from: 'från',
    timeline: '24-timmars vindlinje',
    selectedDate: 'Valt datum',
    selectedHour: 'Vald timme',
    live: 'Just nu',
    airTemperature: 'Lufttemperatur',
    waveHeight: 'Våghöjd',
    swell: 'Dyning',
    score: 'Surfscore',
    report: 'Förhållanderapport',
    wetsuit: 'Våtdräktsrekommendation',
    wetsuitNote: 'Baserad på nuvarande beräknad vattentemperatur och säsong.',
  },
};

const spotPosition = [57.0808889, 12.2576944];
const mapCenter = [57.0831, 12.2518];

const createSpotIcon = () =>
  L.divIcon({
    className: 'leaflet-nautical-icon-wrapper',
    html: `
      <div class="nautical-pin" aria-hidden="true">
        <span class="nautical-pin__ring"></span>
        <span class="nautical-pin__core"></span>
        <span class="nautical-pin__mast"></span>
      </div>
    `,
    iconSize: [34, 48],
    iconAnchor: [17, 42],
  });

const createWindIcon = (rotation, category) =>
  L.divIcon({
    className: 'leaflet-nautical-icon-wrapper',
    html: `
      <div class="wind-pennant wind-pennant--${category}" style="--wind-rotation:${rotation}deg" aria-hidden="true">
        <span class="wind-pennant__shaft"></span>
        <span class="wind-pennant__flag"></span>
        <span class="wind-pennant__tail"></span>
      </div>
    `,
    iconSize: [92, 92],
    iconAnchor: [46, 46],
  });

const SurfMap = ({ spotData, seaTemperature, language, selectedDay, hourlyConditions, selectedHourIndex, onHourChange }) => {
  const text = labels[language] || labels.english;
  const selectedHourlyCondition = hourlyConditions?.[selectedHourIndex] || null;
  const currentWeather = spotData?.weather?.current;
  const activeWindSpeed = selectedHourlyCondition?.windSpeed ?? currentWeather?.wind_speed_10m;
  const activeWindDirection = selectedHourlyCondition?.windDirection ?? currentWeather?.wind_direction_10m ?? 0;
  const activeTemperature = selectedHourlyCondition?.temperature;
  const activeWaveHeight = selectedHourlyCondition?.waveHeight;
  const activeSwellHeight = selectedHourlyCondition?.swellHeight;
  const activeSwellDirection = selectedHourlyCondition?.swellDirection ?? selectedHourlyCondition?.waveDirection ?? 0;
  const activeSwellPeriod = selectedHourlyCondition?.swellPeriod ?? selectedHourlyCondition?.wavePeriod ?? 0;
  const activeSeaTemperature = selectedHourlyCondition?.seaSurfaceTemperature ?? seaTemperature;
  const windCategory = getWindCategory(activeWindDirection);
  const windLabel = getWindCategoryLabel(windCategory, language);
  const directionLabel = degreesToDirection(activeWindDirection);
  const selectedDateLabel = selectedDay ? formatForecastDate(selectedDay.time, language) : text.live;
  const selectedHourLabel = selectedHourlyCondition ? formatHourLabel(selectedHourlyCondition.time, language) : text.live;
  const surfAssessment = getSurfAssessment({
    windDir: activeWindDirection,
    windSpeed: activeWindSpeed,
    swellDir: activeSwellDirection,
    swellPeriod: activeSwellPeriod,
    waveHeight: activeWaveHeight ?? activeSwellHeight,
    seaTemperature: activeSeaTemperature,
    language,
  });
  const surfReport = generateSurfReport({
    score: surfAssessment.score,
    windCategory,
    windDirection: activeWindDirection,
    windSpeed: activeWindSpeed,
    swellDirection: activeSwellDirection,
    swellPeriod: activeSwellPeriod,
    waveHeight: activeWaveHeight ?? activeSwellHeight,
    seaTemperature: activeSeaTemperature,
    language,
  });

  const spotIcon = useMemo(() => createSpotIcon(), []);
  const windIcon = useMemo(
    () => createWindIcon(activeWindDirection, windCategory),
    [activeWindDirection, windCategory]
  );

  return (
    <section className="panel panel-map">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">{text.title}</p>
          <h2>{text.subtitle}</h2>
        </div>
      </div>

      <div className="map-card">
        <div className="leaflet-shell" aria-label={text.title}>
          <div className="map-overlay-card map-overlay-card--bottom">
            <span className="map-overlay-card__label">{text.wind}</span>
            <strong>
              {activeWindSpeed ? `${Math.round(activeWindSpeed)} m/s` : text.calm} · {directionLabel} {text.from} {Math.round(activeWindDirection)}°
            </strong>
          </div>

          <div className={`map-orientation-badge map-orientation-badge--${windCategory}`}>
            {windLabel}
          </div>

          <div className="map-compass" aria-hidden="true">
            <span>N</span>
            <span>E</span>
            <span>S</span>
            <span>W</span>
          </div>

          <MapContainer
            center={mapCenter}
            zoom={13}
            scrollWheelZoom={false}
            dragging={true}
            zoomControl={false}
            className="leaflet-map"
          >
            <ZoomControl position="bottomright" />
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={spotPosition} icon={spotIcon} />
            <Marker position={spotPosition} icon={windIcon} />
          </MapContainer>
        </div>

        <div className="map-timeline-card">
          <div className="map-timeline-card__header">
            <div>
              <p className="eyebrow">{text.timeline}</p>
              <h3>{selectedDateLabel}</h3>
            </div>
            <strong>{selectedHourLabel}</strong>
          </div>

          {hourlyConditions?.length ? (
            <div className="timeline-slider-block">
              <input
                className="timeline-slider"
                type="range"
                min="0"
                max={Math.max(hourlyConditions.length - 1, 0)}
                step="1"
                value={selectedHourIndex}
                onChange={(event) => onHourChange(Number(event.target.value))}
                aria-label={text.timeline}
              />
              <div className="timeline-slider__labels" aria-hidden="true">
                <span>{formatHourLabel(hourlyConditions[0].time, language)}</span>
                <span>{formatHourLabel(hourlyConditions[Math.floor(hourlyConditions.length / 2)].time, language)}</span>
                <span>{formatHourLabel(hourlyConditions[hourlyConditions.length - 1].time, language)}</span>
              </div>
            </div>
          ) : null}

          <div className="surf-score-card">
            <div className="info-section-title">
  <img src={generalIcons.surfScore} alt="Surf score" className="icon-md" />
  <span>Surf score</span>
</div>
            <div className="surf-score-card__header">
              <span>{text.score}</span>
              <strong>{surfAssessment.score}/100 · {surfAssessment.label}</strong>
            </div>
            <p>{surfReport}</p>
          </div>

          <div className="wetsuit-inline-card">
            <div className="wetsuit-inline-card__header">
              <img src={generalIcons.wetsuit} alt="" className="icon-md" />
              <span>{text.wetsuit}</span>
            </div>
            <div className="wetsuit-inline-card__body">
              <WetsuitRecommendation waterTemperature={activeSeaTemperature} language={language} />
            </div>
            <p className="wetsuit-inline-card__note">{text.wetsuitNote}</p>
          </div>

          <div className="map-meta-grid map-meta-grid--timeline">
            <div>
              <span>{text.selectedDate}</span>
              <strong>{selectedDateLabel}</strong>
            </div>
            <div>
              <span>{text.selectedHour}</span>
              <strong>{selectedHourLabel}</strong>
            </div>
            <div>
              <span>{text.wind}</span>
              <strong>{activeWindSpeed ? `${Math.round(activeWindSpeed)} m/s` : text.calm}</strong>
            </div>
            <div>
              <span>{text.direction}</span>
              <strong>{directionLabel} · {Math.round(activeWindDirection)}°</strong>
            </div>
            <div>
              <span>{text.type}</span>
              <strong>{windLabel}</strong>
            </div>
            <div>
              <span>{text.airTemperature}</span>
              <strong>{activeTemperature != null ? `${Math.round(activeTemperature)}°C` : '—'}</strong>
            </div>
            <div>
              <span>{text.waveHeight}</span>
              <strong>{activeWaveHeight != null ? `${activeWaveHeight.toFixed(1)} m` : '—'}</strong>
            </div>
            <div>
              <span>{text.swell}</span>
              <strong>
                {activeSwellHeight != null ? `${activeSwellHeight.toFixed(1)} m` : '—'} · {activeSwellPeriod ? `${activeSwellPeriod.toFixed(0)}s` : '—'}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SurfMap;
